import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  TextField,
  FormControl,
  InputLabel,
  Select,
  Button,
  CircularProgress,
  Tooltip,
  Avatar,
  Paper
} from '@mui/material';
import {
  MoreVert as MoreIcon,
  FilterList as FilterIcon,
  CheckCircle as CheckCircleIcon,
  ArrowForward as ArrowForwardIcon,
  DirectionsCar as DirectionsCarIcon,
  Build as BuildIcon,
  SwapHoriz as SwapHorizIcon,
  Handyman as HandymanIcon,
  MoreHoriz as MoreHorizIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { task, car as carApi } from '../../../utils/api';
import { TASK_STATUS, TASK_PRIORITY, TASK_TYPE } from '../../../constants/task';
import { format } from 'date-fns';
import { toast } from 'react-hot-toast';

const TaskList = () => {
  const { t } = useTranslation('workshop');
  const navigate = useNavigate();
  const [tasks, setTasks] = useState([]);
  const [cars, setCars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    status: '',
    priority: '',
    type: '',
    startDate: '',
    endDate: ''
  });
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedTask, setSelectedTask] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [imageErrors, setImageErrors] = useState({});
  const [carMap, setCarMap] = useState({});

  useEffect(() => {
    fetchData();
  }, [filters]);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Fetch both tasks and cars in parallel
      const [tasksResponse, carsData] = await Promise.all([
        task.getMyTasks(filters),
        carApi.getAllCars()
      ]);
      
      console.log('Task response in TaskList:', tasksResponse);
      console.log('Cars data:', carsData);
      
      // Handle different response formats for tasks
      let taskData = [];
      if (Array.isArray(tasksResponse)) {
        taskData = tasksResponse;
      } else if (tasksResponse && tasksResponse.tasks && Array.isArray(tasksResponse.tasks)) {
        taskData = tasksResponse.tasks;
      } else if (tasksResponse && typeof tasksResponse === 'object') {
        console.log('Unexpected task data format, attempting to extract tasks');
        const possibleTaskArrays = Object.values(tasksResponse).filter(val => Array.isArray(val));
        if (possibleTaskArrays.length > 0) {
          // Use the largest array as the tasks array
          taskData = possibleTaskArrays.reduce((a, b) => a.length > b.length ? a : b, []);
        }
      }
      
      // Create a map of cars by ID for easy lookup
      const carsById = {};
      if (Array.isArray(carsData)) {
        carsData.forEach(car => {
          if (car && car._id) {
            carsById[car._id] = car;
          }
        });
      }
      
      setCarMap(carsById);
      console.log('Cars by ID map:', carsById);
      
      // Enhance task data with complete car information
      const enhancedTasks = taskData.map(task => {
        if (task.car && task.car._id && carsById[task.car._id]) {
          // Replace the partial car data with the complete car data
          return {
            ...task,
            car: carsById[task.car._id]
          };
        }
        return task;
      });
      
      console.log('Enhanced tasks with complete car data:', enhancedTasks);
      
      // Sort tasks so completed tasks appear at the end
      enhancedTasks.sort((a, b) => {
        if (a.status === TASK_STATUS.COMPLETED && b.status !== TASK_STATUS.COMPLETED) {
          return 1; // a comes after b
        } else if (a.status !== TASK_STATUS.COMPLETED && b.status === TASK_STATUS.COMPLETED) {
          return -1; // a comes before b
        } else {
          // If both are completed or both are not completed, sort by due date (if available)
          if (a.dueDate && b.dueDate) {
            return new Date(a.dueDate) - new Date(b.dueDate);
          } else if (a.dueDate) {
            return -1; // a comes first if it has a due date
          } else if (b.dueDate) {
            return 1; // b comes first if it has a due date
          }
          return 0; // maintain original order
        }
      });
      
      setTasks(enhancedTasks);
      setCars(carsData || []);
      
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error(t('error.loadTasks'));
    } finally {
      setLoading(false);
    }
  };

  const handleMenuOpen = (event, task) => {
    event.stopPropagation(); // Prevent card click event
    setAnchorEl(event.currentTarget);
    setSelectedTask(task);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedTask(null);
  };

  const handleStatusChange = async (newStatus) => {
    try {
      await task.update(selectedTask._id, { status: newStatus });
      toast.success(t('success.taskUpdated'));
      fetchData();
    } catch (error) {
      console.error('Error updating task status:', error);
      toast.error(t('error.updateTask'));
    }
    handleMenuClose();
  };

  const handleFilterChange = (event) => {
    const { name, value } = event.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const getStatusColor = (status) => {
    switch (status) {
      case TASK_STATUS.PENDING:
        return 'warning';
      case TASK_STATUS.IN_PROGRESS:
        return 'info';
      case TASK_STATUS.COMPLETED:
        return 'success';
      case TASK_STATUS.CANCELLED:
        return 'error';
      default:
        return 'default';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case TASK_PRIORITY.LOW:
        return 'success';
      case TASK_PRIORITY.MEDIUM:
        return 'info';
      case TASK_PRIORITY.HIGH:
        return 'warning';
      case TASK_PRIORITY.URGENT:
        return 'error';
      default:
        return 'default';
    }
  };

  const getTaskTypeIcon = (type) => {
    switch (type) {
      case TASK_TYPE.MAINTENANCE:
        return <BuildIcon fontSize="small" />;
      case TASK_TYPE.REPLACEMENT:
        return <SwapHorizIcon fontSize="small" />;
      case TASK_TYPE.FIX:
        return <HandymanIcon fontSize="small" />;
      case TASK_TYPE.OTHER:
        return <MoreHorizIcon fontSize="small" />;
      default:
        return <BuildIcon fontSize="small" />;
    }
  };

  const handleImageError = (carId) => {
    setImageErrors(prev => ({
      ...prev,
      [carId]: true
    }));
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={3}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h5" component="h2">
          {t('taskList.title')}
        </Typography>
        <Button
          startIcon={<FilterIcon />}
          onClick={() => setShowFilters(!showFilters)}
          color="primary"
        >
          {showFilters ? t('common.hideFilters') : t('common.showFilters')}
        </Button>
      </Box>

      {showFilters && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              {t('taskList.filters')}
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth>
                  <InputLabel>{t('taskList.filterStatus')}</InputLabel>
                  <Select
                    name="status"
                    value={filters.status}
                    onChange={handleFilterChange}
                    label={t('taskList.filterStatus')}
                  >
                    <MenuItem value="">{t('taskList.all')}</MenuItem>
                    {Object.entries(TASK_STATUS).map(([key, value]) => (
                      <MenuItem key={value} value={value}>
                        {t(`status.${key.toLowerCase()}`)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth>
                  <InputLabel>{t('taskList.filterPriority')}</InputLabel>
                  <Select
                    name="priority"
                    value={filters.priority}
                    onChange={handleFilterChange}
                    label={t('taskList.filterPriority')}
                  >
                    <MenuItem value="">{t('taskList.all')}</MenuItem>
                    {Object.entries(TASK_PRIORITY).map(([key, value]) => (
                      <MenuItem key={value} value={value}>
                        {t(`priority.${key.toLowerCase()}`)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth>
                  <InputLabel>{t('taskList.filterType')}</InputLabel>
                  <Select
                    name="type"
                    value={filters.type}
                    onChange={handleFilterChange}
                    label={t('taskList.filterType')}
                  >
                    <MenuItem value="">{t('taskList.all')}</MenuItem>
                    {Object.entries(TASK_TYPE).map(([key, value]) => (
                      <MenuItem key={value} value={value}>
                        {t(`taskType.${key.toLowerCase()}`)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  name="startDate"
                  label={t('taskList.filterStartDate')}
                  type="date"
                  value={filters.startDate}
                  onChange={handleFilterChange}
                  fullWidth
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  name="endDate"
                  label={t('taskList.filterEndDate')}
                  type="date"
                  value={filters.endDate}
                  onChange={handleFilterChange}
                  fullWidth
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}

      {loading ? (
        <Box display="flex" justifyContent="center" p={3}>
          <CircularProgress />
        </Box>
      ) : tasks.length === 0 ? (
        <Box textAlign="center" p={3}>
          <Typography variant="body1" color="textSecondary">
            {t('taskList.noTasksFound')}
          </Typography>
        </Box>
      ) : (
        <Grid container spacing={2}>
          {tasks.map((task) => {
            // Extract car data safely
            const car = task.car || {};
            const carId = car._id || '';
            const carName = car.name || '';
            const carBrand = car.brand || '';
            const carModel = car.model || '';
            const carYear = car.year || '';
            const carImageUrl = car.imageUrl || '';
            
            // Determine what to display for car name
            let displayCarName = 'Unknown Car';
            if (carName) {
              displayCarName = carName;
            } else if (carBrand && carModel) {
              displayCarName = `${carBrand} ${carModel}`;
            } else if (carBrand) {
              displayCarName = carBrand;
            } else if (carModel) {
              displayCarName = carModel;
            }
            
            return (
              <Grid item xs={12} key={task._id}>
                <Card 
                  sx={{ 
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: 3
                    },
                    opacity: task.status === TASK_STATUS.COMPLETED ? 0.7 : 1,
                    display: 'flex',
                    overflow: 'hidden'
                  }}
                  onClick={() => navigate(`/workshop/tasks/${task._id}`)}
                >
                  {/* Car Image */}
                  <Box 
                    sx={{ 
                      width: 200, 
                      position: 'relative',
                      overflow: 'hidden',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: 'action.hover'
                    }}
                  >
                    {carImageUrl && !imageErrors[carId] ? (
                      <img 
                        src={carImageUrl} 
                        alt={displayCarName}
                        style={{ 
                          width: '100%', 
                          height: '100%', 
                          objectFit: 'cover',
                          objectPosition: 'center'
                        }}
                        onError={() => handleImageError(carId)}
                      />
                    ) : (
                      <Box sx={{ color: 'text.secondary', fontSize: '2.5rem', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                        <Box>🚗</Box>
                        {carBrand && (
                          <Typography variant="caption" sx={{ fontSize: '0.7rem', textAlign: 'center' }}>
                            {carBrand}
                          </Typography>
                        )}
                      </Box>
                    )}
                    
                    {/* Overlay for completed tasks */}
                    {task.status === TASK_STATUS.COMPLETED && (
                      <Box 
                        sx={{ 
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          right: 0,
                          bottom: 0,
                          bgcolor: 'rgba(0, 0, 0, 0.3)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                      >
                        <CheckCircleIcon sx={{ color: 'white', fontSize: '2rem', opacity: 0.8 }} />
                      </Box>
                    )}
                  </Box>
                  
                  <CardContent sx={{ flexGrow: 1, p: 2 }}>
                    <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                      <Box>
                        <Typography variant="h6" component="h3" gutterBottom>
                          {task.title}
                        </Typography>
                        <Box display="flex" flexWrap="wrap" gap={1} mb={1}>
                          <Chip 
                            label={t(`status.${task.status}`)}
                            color={getStatusColor(task.status)}
                            size="small"
                          />
                          <Chip 
                            label={t(`priority.${task.priority}`)}
                            color={getPriorityColor(task.priority)}
                            size="small"
                          />
                          <Chip 
                            icon={getTaskTypeIcon(task.type)}
                            label={t(`taskType.${task.type || TASK_TYPE.MAINTENANCE}`)}
                            size="small"
                            variant="outlined"
                          />
                        </Box>
                        <Box display="flex" alignItems="center" gap={1}>
                          <Typography variant="body2" color="text.secondary" fontWeight="medium">
                            {displayCarName}
                            {carYear && ` (${carYear})`}
                          </Typography>
                        </Box>
                        {task.dueDate && (
                          <Typography variant="body2" color="text.secondary">
                            Due: {format(new Date(task.dueDate), 'MMM dd, yyyy')}
                          </Typography>
                        )}
                      </Box>
                      <Box display="flex" alignItems="center">
                        {task.status !== TASK_STATUS.COMPLETED && (
                          <Tooltip title="View Details">
                            <IconButton 
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/workshop/tasks/${task._id}`);
                              }}
                            >
                              <ArrowForwardIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => handleMenuClose()}
      >
        <MenuItem onClick={() => handleStatusChange(TASK_STATUS.COMPLETED)}>
          Mark as Completed
        </MenuItem>
        <MenuItem onClick={() => navigate(`/workshop/tasks/${selectedTask?._id}`)}>
          View Details
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default TaskList; 